import { action, IObservableArray, observable, toJS } from 'mobx';
import api from './api';
import { SubTenantInterface, TenantInterface } from '@app/modules/Tenants/Tenant.interfaces';
import { notification } from 'antd';
import { getDefaultSchedulePolicy, decodePolicyFromSchedule } from './schedulePolicyEncoder';

const blankTenant: Omit<TenantInterface, 'id'> = { name: '' };

// Create blank subtenant with properly decoded defaults
function createBlankSubTenant(): Omit<SubTenantInterface, 'id'> {
  const defaultPolicy = getDefaultSchedulePolicy();
  const decodedPolicy = decodePolicyFromSchedule(defaultPolicy);

  return {
    name: '',
    tenantId: 0,
    levelId: 0,
    defProf: null,
    cronProfileId: null,
    schedulePolicy: defaultPolicy,

    // decoded defaults from schedulePolicy
    setpointOnMode: decodedPolicy.setpointOnMode,
    setpointOffMode: decodedPolicy.setpointOffMode,
    hardModeOn: decodedPolicy.hardModeOn,
    hardModeOff: decodedPolicy.hardModeOff,
    motionSensorOn: decodedPolicy.motionSensorOn,
    motionSensorOff: decodedPolicy.motionSensorOff,
    longNoMotionOn: decodedPolicy.longNoMotionOn,
    longNoMotionOff: decodedPolicy.longNoMotionOff,
    occupancyTriggeredActivationOn: decodedPolicy.occupancyTriggeredActivationOn,
    occupancyTriggeredActivationOff: decodedPolicy.occupancyTriggeredActivationOff,

    // extra policy toggles
    enforceMinimalSetpoint: decodedPolicy.enforceMinimalSetpoint,
    softOperationOn: decodedPolicy.softOperationOn,
    softOperationOff: decodedPolicy.softOperationOff,
    enforceLocalDashboardOffline: decodedPolicy.enforceLocalDashboardOffline,

    motionThreshold: 2500,
    motionHolding: 20,
    motionAutostop: 0
  };
}

const blankSubTenant: Omit<SubTenantInterface, 'id'> = createBlankSubTenant();

export default class TenantsStore {
  @observable loading = false;
  @observable subTenantsLoading = false;
  @observable listTenants: IObservableArray<TenantInterface> = observable.array([]);
  @observable selectedTenant?: TenantInterface;
  @observable subTenantsMap: { [key: number]: IObservableArray<SubTenantInterface> } = {};
  private listEditingTenants: Map<number, TenantInterface> = new Map();
  private listEditingSubTenants: Map<number, SubTenantInterface> = new Map();

  @action.bound public setSelectedTenant(item: TenantInterface) {
    this.selectedTenant = item;
  }

  @action.bound public async getTenants() {
    try {
      this.loading = true;
      this.listTenants = await api.getTenants();
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.loading = false;
    }
  }

  @action.bound public async getSubTenants(tenantId) {
    try {
      this.subTenantsLoading = true;
      const subtenants = await api.getSubtenants(tenantId);
      // Decode schedulePolicy for each subtenant to populate individual policy fields
      const decodedSubtenants = subtenants.map(subtenant => {
        if (subtenant.schedulePolicy !== undefined) {
          const decodedPolicy = decodePolicyFromSchedule(subtenant.schedulePolicy);
          return {
            ...subtenant,
            ...decodedPolicy
          };
        }
        return subtenant;
      });
      this.subTenantsMap[tenantId] = decodedSubtenants;
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.subTenantsLoading = false;
    }
  }

  @action.bound public removeTenant(item) {
    this.listTenants.remove(item);
  }

  @action.bound public removeSubTenant(item) {
    if (this.selectedTenant) this.subTenantsMap[this.selectedTenant.id].remove(item);
  }

  @action.bound public saveBackupTenant(item) {
    this.listEditingTenants.set(item.id, toJS(item));
  }

  @action.bound public saveBackupSubTenant(item) {
    this.listEditingSubTenants.set(item.id, toJS(item));
  }

  @action.bound public restoreBackedUpTenant(item) {
    if (this.listEditingTenants.has(item.id)) {
      const foundBackup = this.listEditingTenants.get(item.id) as TenantInterface;
      this.listEditingTenants.delete(item.id);
      const found = this.listTenants.find(tenant => tenant.id === item.id);
      if (found) {
        Object.assign(found, foundBackup);
      }
    }
  }

  @action.bound public restoreBackedUpSubTenant(item) {
    if (this.selectedTenant && this.listEditingSubTenants.has(item.id)) {
      const foundBackup = this.listEditingSubTenants.get(item.id) as SubTenantInterface;
      this.listEditingSubTenants.delete(item.id);
      const found = this.subTenantsMap[this.selectedTenant.id].find(tenant => tenant.id === item.id);
      if (found) {
        Object.assign(found, foundBackup);
      }
    }
  }

  @action.bound public addBlankTenant() {
    // @ts-ignore
    this.listTenants.push(blankTenant);
  }

  @action.bound public addBlankSubTenant(tenantId) {
    if (tenantId) {
      // @ts-ignore
      this.subTenantsMap[tenantId].push({ ...blankSubTenant, tenantId });
    }
  }
}
